import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Chart } from "chart.js/auto";
import {
  Activity,
  Award,
  BarChart3,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Gauge,
  LayoutDashboard,
  RefreshCw,
  Server,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  TrendingUp,
  UserPlus,
  Users,
  Wifi,
  XCircle,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import SuperAdminDashboard from "./SuperAdminDashboard";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const EMPTY_DASHBOARD = {
  generatedAt: null,
  totals: {
    certifs: 0,
    clients: 0,
    participants: 0,
    pendingRequests: 0,
  },
  comparisons: {
    certifs30Days: { current: 0, previous: 0, growth: 0 },
    participants30Days: { current: 0, previous: 0, growth: 0 },
  },
  performance: {
    successCount: 0,
    failureCount: 0,
    successRate: 0,
    activeClients: 0,
    inactiveClients: 0,
    activeClientRate: 0,
    averageDuration: 0,
    averageCertifsPerClient: 0,
    averageCertifsPerParticipant: 0,
    dailyAverage30Days: 0,
    topClientShare: 0,
  },
  requests: { pending: 0, approved: 0, rejected: 0 },
  monthlyTrend: [],
  topClients: [],
  themes: [],
  recentActivity: [],
};

const numberFormatter = new Intl.NumberFormat("fr-FR");

const formatNumber = (value) => numberFormatter.format(Number(value) || 0);

const formatPercent = (value) => `${Number(value || 0).toFixed(1)} %`;

const formatMonth = (value) => {
  if (!value) return "—";
  const [year, month] = value.split("-").map(Number);
  if (!year || !month) return value;
  return new Intl.DateTimeFormat("fr-FR", {
    month: "short",
    year: "2-digit",
  }).format(new Date(year, month - 1, 1));
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const chartTextColor = "#64748b";
const chartGridColor = "rgba(148, 163, 184, 0.16)";

const KpiCard = ({
  title,
  value,
  detail,
  icon: Icon,
  trend,
  accent = "blue",
  onClick,
}) => {
  const styles = {
    blue: {
      icon: "bg-blue-50 text-blue-700 ring-blue-100",
      glow: "from-blue-500/15",
    },
    cyan: {
      icon: "bg-cyan-50 text-cyan-700 ring-cyan-100",
      glow: "from-cyan-500/15",
    },
    emerald: {
      icon: "bg-emerald-50 text-emerald-700 ring-emerald-100",
      glow: "from-emerald-500/15",
    },
    violet: {
      icon: "bg-violet-50 text-violet-700 ring-violet-100",
      glow: "from-violet-500/15",
    },
  }[accent];

  const TrendIcon = Number(trend) >= 0 ? TrendingUp : TrendingDown;
  const trendClass =
    Number(trend) >= 0
      ? "bg-emerald-50 text-emerald-700"
      : "bg-rose-50 text-rose-700";

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg disabled:cursor-default"
    >
      <div
        className={`pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b ${styles.glow} to-transparent opacity-70`}
      />
      <div className="relative flex items-start justify-between gap-4">
        <div className={`rounded-xl p-3 ring-1 ${styles.icon}`}>
          <Icon className="h-5 w-5" />
        </div>
        {trend !== undefined && trend !== null ? (
          <div
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${trendClass}`}
          >
            <TrendIcon className="h-3.5 w-3.5" />
            {Math.abs(Number(trend) || 0).toFixed(1)} %
          </div>
        ) : null}
      </div>
      <div className="relative mt-5">
        <p className="text-sm font-semibold text-slate-500">{title}</p>
        <p className="mt-1 text-3xl font-black tracking-tight text-slate-950">
          {value}
        </p>
        <p className="mt-2 text-xs leading-relaxed text-slate-500">{detail}</p>
      </div>
    </button>
  );
};

const MetricTile = ({ icon: Icon, label, value, hint, accent = "blue" }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    violet: "bg-violet-50 text-violet-700",
    amber: "bg-amber-50 text-amber-700",
  };

  return (
    <div className="flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/80 p-3.5">
      <div className={`shrink-0 rounded-lg p-2 ${colors[accent]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="truncate text-xs font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </p>
        <div className="mt-0.5 flex flex-wrap items-baseline gap-2">
          <span className="text-lg font-black text-slate-900">{value}</span>
          {hint ? <span className="text-xs text-slate-500">{hint}</span> : null}
        </div>
      </div>
    </div>
  );
};

const ChartPanel = ({ title, subtitle, icon: Icon, children, action }) => (
  <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
      <div className="flex min-w-0 items-start gap-3">
        <div className="rounded-lg bg-blue-50 p-2 text-blue-700">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <h2 className="font-bold text-slate-900">{title}</h2>
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        </div>
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const ProgressMetric = ({ label, value, detail, tone = "blue" }) => {
  const barColor = {
    blue: "bg-blue-600",
    emerald: "bg-emerald-500",
    violet: "bg-violet-500",
    amber: "bg-amber-500",
  }[tone];

  const normalized = Math.min(Math.max(Number(value) || 0, 0), 100);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-700">{label}</p>
          <p className="text-xs text-slate-500">{detail}</p>
        </div>
        <span className="text-sm font-black text-slate-900">
          {normalized.toFixed(1)} %
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-all duration-700 ${barColor}`}
          style={{ width: `${normalized}%` }}
        />
      </div>
    </div>
  );
};

const OrganizationAdminDashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [latency, setLatency] = useState(null);
  const [isOperational, setIsOperational] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const monthlyRef = useRef(null);
  const clientsRef = useRef(null);
  const themesRef = useRef(null);
  const outcomeRef = useRef(null);
  const chartInstances = useRef({});

  const tr = (key, fallback) => t?.[key] || fallback;

  const loadDashboard = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (!token || !storedUser) {
        navigate("/login", { replace: true });
        return;
      }

      const user = JSON.parse(storedUser);
      if (user?.type !== "Admin") {
        navigate("/unauthorized", { replace: true });
        return;
      }

      const startedAt = Date.now();
      const response = await axios.get(`${API_URL}/api/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const elapsed = Date.now() - startedAt;

      setDashboard({ ...EMPTY_DASHBOARD, ...response.data });
      setLatency(elapsed);
      setIsOperational(true);
      setLastUpdated(new Date());
    } catch (requestError) {
      console.error("Dashboard error:", requestError);
      setIsOperational(false);
      setError(
        requestError.response?.data?.message ||
          "Impossible de charger les données du dashboard."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadDashboard({ silent: true });
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  const chartData = useMemo(
    () => ({
      months: dashboard.monthlyTrend || [],
      clients: dashboard.topClients || [],
      themes: dashboard.themes || [],
      outcomes: [
        {
          label: tr("success", "Réussite"),
          value: dashboard.performance?.successCount || 0,
        },
        {
          label: tr("failure", "Échec"),
          value: dashboard.performance?.failureCount || 0,
        },
      ],
    }),
    [dashboard, t]
  );

  useEffect(() => {
    if (loading) return undefined;

    const destroy = (key) => {
      chartInstances.current[key]?.destroy();
      delete chartInstances.current[key];
    };

    const commonPlugins = {
      legend: {
        labels: {
          color: chartTextColor,
          usePointStyle: true,
          boxWidth: 8,
          padding: 16,
          font: { size: 11, weight: "600" },
        },
      },
    };

    if (monthlyRef.current) {
      destroy("monthly");
      const context = monthlyRef.current.getContext("2d");
      const gradient = context.createLinearGradient(0, 0, 0, 280);
      gradient.addColorStop(0, "rgba(37, 99, 235, 0.28)");
      gradient.addColorStop(1, "rgba(37, 99, 235, 0.01)");

      chartInstances.current.monthly = new Chart(context, {
        type: "line",
        data: {
          labels: chartData.months.map((item) => formatMonth(item.month)),
          datasets: [
            {
              label: "Certificats générés",
              data: chartData.months.map((item) => item.count),
              borderColor: "#2563eb",
              backgroundColor: gradient,
              fill: true,
              tension: 0.38,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: "#ffffff",
              pointBorderColor: "#2563eb",
              pointBorderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { intersect: false, mode: "index" },
          plugins: { ...commonPlugins, legend: { display: false } },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: chartTextColor, font: { size: 11 } },
            },
            y: {
              beginAtZero: true,
              grid: { color: chartGridColor },
              ticks: {
                color: chartTextColor,
                precision: 0,
                font: { size: 11 },
              },
            },
          },
        },
      });
    }

    if (clientsRef.current) {
      destroy("clients");
      chartInstances.current.clients = new Chart(clientsRef.current, {
        type: "bar",
        data: {
          labels: chartData.clients.map((item) => item.name),
          datasets: [
            {
              label: "Certificats",
              data: chartData.clients.map((item) => item.count),
              backgroundColor: "rgba(14, 116, 144, 0.82)",
              hoverBackgroundColor: "#0e7490",
              borderRadius: 8,
              borderSkipped: false,
              barThickness: 17,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: { ...commonPlugins, legend: { display: false } },
          scales: {
            x: {
              beginAtZero: true,
              grid: { color: chartGridColor },
              ticks: { color: chartTextColor, precision: 0 },
            },
            y: {
              grid: { display: false },
              ticks: { color: "#334155", font: { size: 11, weight: "600" } },
            },
          },
        },
      });
    }

    if (themesRef.current) {
      destroy("themes");
      chartInstances.current.themes = new Chart(themesRef.current, {
        type: "doughnut",
        data: {
          labels: chartData.themes.map((item) => item.name),
          datasets: [
            {
              data: chartData.themes.map((item) => item.count),
              backgroundColor: [
                "#2563eb",
                "#0891b2",
                "#10b981",
                "#8b5cf6",
                "#f59e0b",
                "#f43f5e",
                "#64748b",
              ],
              borderColor: "#ffffff",
              borderWidth: 3,
              hoverOffset: 5,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "66%",
          plugins: {
            ...commonPlugins,
            legend: {
              ...commonPlugins.legend,
              position: "bottom",
            },
          },
        },
      });
    }

    if (outcomeRef.current) {
      destroy("outcome");
      chartInstances.current.outcome = new Chart(outcomeRef.current, {
        type: "doughnut",
        data: {
          labels: chartData.outcomes.map((item) => item.label),
          datasets: [
            {
              data: chartData.outcomes.map((item) => item.value),
              backgroundColor: ["#10b981", "#f43f5e"],
              borderColor: "#ffffff",
              borderWidth: 4,
              hoverOffset: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "76%",
          plugins: {
            ...commonPlugins,
            legend: {
              ...commonPlugins.legend,
              position: "bottom",
            },
          },
        },
      });
    }

    return () => {
      Object.values(chartInstances.current).forEach((instance) =>
        instance.destroy()
      );
      chartInstances.current = {};
    };
  }, [chartData, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-[1600px] animate-pulse space-y-6">
          <div className="h-44 rounded-3xl bg-slate-200" />
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="h-44 rounded-2xl bg-slate-200" />
            ))}
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="h-96 rounded-2xl bg-slate-200 lg:col-span-2" />
            <div className="h-96 rounded-2xl bg-slate-200" />
          </div>
        </div>
      </div>
    );
  }

  const { totals, comparisons, performance: performanceMetrics, requests } = dashboard;

  return (
    <div className="min-h-screen bg-slate-50 p-3 sm:p-5 lg:p-8">
      <div className="mx-auto max-w-[1600px] space-y-6">
        <header className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-900 px-5 py-6 text-white shadow-xl sm:px-7 sm:py-8">
          <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />

          <div className="relative flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-cyan-100 backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5" />
                Centre de pilotage
              </div>
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-white/10 p-3 ring-1 ring-white/15 backdrop-blur-sm">
                  <LayoutDashboard className="h-7 w-7 text-cyan-200" />
                </div>
                <div>
                  <h1 className="text-2xl font-black tracking-tight sm:text-3xl">
                    {tr("dashboardTitle", "Tableau de bord")}
                  </h1>
                  <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-300">
                    Vue synthétique des certificats, participants, clients et demandes de CertiLink.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="rounded-xl border border-white/15 bg-white/10 px-3.5 py-2.5 backdrop-blur-sm">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-300">
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      isOperational
                        ? "bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.9)]"
                        : "bg-rose-400"
                    }`}
                  />
                  {isOperational ? "Système opérationnel" : "Incident détecté"}
                </div>
                <p className="mt-1 text-xs text-slate-400">
                  {latency !== null ? `${latency} ms` : "Latence indisponible"}
                  {lastUpdated
                    ? ` · actualisé à ${lastUpdated.toLocaleTimeString("fr-FR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}`
                    : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => loadDashboard({ silent: true })}
                disabled={refreshing}
                className="inline-flex h-11 items-center gap-2 rounded-xl bg-white px-4 text-sm font-bold text-slate-900 shadow-sm transition hover:bg-cyan-50 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <RefreshCw
                  className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
                />
                Actualiser
              </button>
            </div>
          </div>
        </header>

        {error ? (
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 shrink-0" />
              {error}
            </div>
            <button
              type="button"
              onClick={() => loadDashboard({ silent: true })}
              className="font-bold underline underline-offset-2"
            >
              Réessayer
            </button>
          </div>
        ) : null}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            title="Certificats"
            value={formatNumber(totals.certifs)}
            detail={`${formatNumber(comparisons.certifs30Days.current)} créés sur les 30 derniers jours`}
            icon={Award}
            trend={comparisons.certifs30Days.growth}
            accent="blue"
            onClick={() => navigate("/participants")}
          />
          <KpiCard
            title="Participants"
            value={formatNumber(totals.participants)}
            detail={`${formatNumber(comparisons.participants30Days.current)} nouveaux sur 30 jours`}
            icon={Users}
            trend={comparisons.participants30Days.growth}
            accent="cyan"
            onClick={() => navigate("/participants")}
          />
          <KpiCard
            title="Clients"
            value={formatNumber(totals.clients)}
            detail={`${formatPercent(performanceMetrics.activeClientRate)} des comptes sont actifs`}
            icon={Building2}
            accent="emerald"
            onClick={() => navigate("/clients")}
          />
          <KpiCard
            title="Demandes en attente"
            value={formatNumber(totals.pendingRequests)}
            detail={`${formatNumber(requests.approved)} approuvées · ${formatNumber(requests.rejected)} rejetées`}
            icon={Clock3}
            accent="violet"
            onClick={() => navigate("/admin/requests")}
          />
        </section>

        <section className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-2 xl:grid-cols-4">
          <MetricTile
            icon={CheckCircle2}
            label="Taux de réussite"
            value={formatPercent(performanceMetrics.successRate)}
            hint={`${formatNumber(performanceMetrics.successCount)} certificats`}
            accent="emerald"
          />
          <MetricTile
            icon={Gauge}
            label="Rythme quotidien"
            value={formatNumber(performanceMetrics.dailyAverage30Days)}
            hint="certificats / jour"
            accent="blue"
          />
          <MetricTile
            icon={CalendarDays}
            label="Durée moyenne"
            value={formatNumber(performanceMetrics.averageDuration)}
            hint="jours / formation"
            accent="violet"
          />
          <MetricTile
            icon={BarChart3}
            label="Moyenne par client"
            value={formatNumber(performanceMetrics.averageCertifsPerClient)}
            hint="certificats / client"
            accent="amber"
          />
        </section>

        <section className="grid gap-6 xl:grid-cols-3">
          <div className="xl:col-span-2">
            <ChartPanel
              title="Évolution de la production"
              subtitle="Nombre de certificats générés au cours des six derniers mois"
              icon={Activity}
              action={
                <div className="rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700">
                  {formatNumber(comparisons.certifs30Days.current)} sur 30 jours
                </div>
              }
            >
              <div className="h-[300px] sm:h-[340px]">
                <canvas ref={monthlyRef} />
              </div>
            </ChartPanel>
          </div>

          <ChartPanel
            title="Qualité des résultats"
            subtitle="Répartition réussite et échec"
            icon={ShieldCheck}
          >
            <div className="relative mx-auto h-[250px] max-w-[320px]">
              <canvas ref={outcomeRef} />
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pb-7">
                <span className="text-3xl font-black text-slate-950">
                  {formatPercent(performanceMetrics.successRate)}
                </span>
                <span className="text-xs font-semibold text-slate-500">
                  de réussite
                </span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-emerald-50 p-3 text-center">
                <p className="text-xl font-black text-emerald-800">
                  {formatNumber(performanceMetrics.successCount)}
                </p>
                <p className="text-xs font-semibold text-emerald-700">Réussites</p>
              </div>
              <div className="rounded-xl bg-rose-50 p-3 text-center">
                <p className="text-xl font-black text-rose-800">
                  {formatNumber(performanceMetrics.failureCount)}
                </p>
                <p className="text-xs font-semibold text-rose-700">Échecs</p>
              </div>
            </div>
          </ChartPanel>
        </section>

        <section className="grid gap-6 xl:grid-cols-2">
          <ChartPanel
            title="Clients les plus actifs"
            subtitle="Classement par nombre de certificats générés"
            icon={Building2}
            action={
              <span className="text-xs font-semibold text-slate-500">
                Top client : {formatPercent(performanceMetrics.topClientShare)} du volume
              </span>
            }
          >
            <div className="h-[310px]">
              <canvas ref={clientsRef} />
            </div>
          </ChartPanel>

          <ChartPanel
            title="Répartition par domaine"
            subtitle="Principaux thèmes représentés dans les certificats"
            icon={Award}
          >
            <div className="h-[310px]">
              <canvas ref={themesRef} />
            </div>
          </ChartPanel>
        </section>

        <section className="grid gap-6 xl:grid-cols-3">
          <div className="xl:col-span-2">
            <ChartPanel
              title="Activité récente"
              subtitle="Derniers certificats créés sur la plateforme"
              icon={Sparkles}
              action={
                <button
                  type="button"
                  onClick={() => navigate("/participants")}
                  className="text-xs font-bold text-blue-700 transition hover:text-blue-900"
                >
                  Voir les participants →
                </button>
              }
            >
              {dashboard.recentActivity.length === 0 ? (
                <div className="flex min-h-52 flex-col items-center justify-center text-center text-slate-500">
                  <Award className="mb-3 h-9 w-9 text-slate-300" />
                  <p className="font-semibold">Aucune activité récente</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] border-separate border-spacing-0">
                    <thead>
                      <tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                        <th className="border-b border-slate-200 px-3 py-3">Certificat</th>
                        <th className="border-b border-slate-200 px-3 py-3">Participant</th>
                        <th className="border-b border-slate-200 px-3 py-3">Client</th>
                        <th className="border-b border-slate-200 px-3 py-3">Résultat</th>
                        <th className="border-b border-slate-200 px-3 py-3 text-right">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.recentActivity.map((item) => (
                        <tr key={item._id} className="group text-sm">
                          <td className="border-b border-slate-100 px-3 py-3.5">
                            <p className="font-bold text-slate-900">{item.theme}</p>
                            <p className="mt-0.5 font-mono text-xs text-slate-400">
                              {item.identifier}
                            </p>
                          </td>
                          <td className="border-b border-slate-100 px-3 py-3.5 font-semibold text-slate-700">
                            {item.participantName}
                          </td>
                          <td className="border-b border-slate-100 px-3 py-3.5 text-slate-600">
                            {item.clientName}
                          </td>
                          <td className="border-b border-slate-100 px-3 py-3.5">
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${
                                item.success
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-rose-50 text-rose-700"
                              }`}
                            >
                              {item.success ? (
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              ) : (
                                <XCircle className="h-3.5 w-3.5" />
                              )}
                              {item.success ? "Réussite" : "Échec"}
                            </span>
                          </td>
                          <td className="border-b border-slate-100 px-3 py-3.5 text-right text-xs text-slate-500">
                            {formatDateTime(item.date)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ChartPanel>
          </div>

          <div className="space-y-6">
            <ChartPanel
              title="Santé de la plateforme"
              subtitle="Indicateurs opérationnels essentiels"
              icon={Server}
            >
              <div className="space-y-5">
                <ProgressMetric
                  label="Clients actifs"
                  value={performanceMetrics.activeClientRate}
                  detail={`${formatNumber(performanceMetrics.activeClients)} actifs sur ${formatNumber(totals.clients)}`}
                  tone="emerald"
                />
                <ProgressMetric
                  label="Taux de réussite"
                  value={performanceMetrics.successRate}
                  detail={`${formatNumber(performanceMetrics.successCount)} réussites enregistrées`}
                  tone="blue"
                />
                <ProgressMetric
                  label="Concentration du top client"
                  value={performanceMetrics.topClientShare}
                  detail="Part du volume détenue par le client le plus actif"
                  tone="violet"
                />
              </div>
            </ChartPanel>

            <div
              className={`overflow-hidden rounded-2xl p-5 text-white shadow-lg ${
                isOperational
                  ? "bg-gradient-to-br from-blue-700 via-cyan-700 to-emerald-700"
                  : "bg-gradient-to-br from-rose-700 to-red-900"
              }`}
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/70">
                    Service API
                  </p>
                  <h2 className="mt-1 text-xl font-black">
                    {isOperational ? "Disponible" : "Indisponible"}
                  </h2>
                </div>
                <div className="rounded-xl bg-white/15 p-3 backdrop-blur-sm">
                  {isOperational ? (
                    <Wifi className="h-6 w-6" />
                  ) : (
                    <Server className="h-6 w-6" />
                  )}
                </div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
                  <p className="text-xs text-white/70">Latence</p>
                  <p className="mt-1 text-xl font-black">
                    {latency !== null ? `${latency} ms` : "—"}
                  </p>
                </div>
                <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
                  <p className="text-xs text-white/70">Certif. / participant</p>
                  <p className="mt-1 text-xl font-black">
                    {formatNumber(performanceMetrics.averageCertifsPerParticipant)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

const isSuperAdminSession = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user?.isSuperAdmin || user?.adminRole === "SuperAdmin") return true;

    const token = localStorage.getItem("token");
    const payload = token?.split(".")?.[1];
    if (!payload) return false;
    const normalized = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payload.length / 4) * 4, "=");
    const decoded = JSON.parse(atob(normalized));
    return decoded?.isSuperAdmin || decoded?.adminRole === "SuperAdmin";
  } catch {
    return false;
  }
};

const Dashboard = () =>
  isSuperAdminSession() ? (
    <SuperAdminDashboard />
  ) : (
    <OrganizationAdminDashboard />
  );

export default Dashboard;
